/*
Home block "The next two weeks": this week and the next as two strips, Monday to Sunday.
Each strip starts with its date range; a day shows up to two event chips and "+N more",
today is marked, a day with an event on air right now turns dark. Only events with a
known day are placed — a rumour of a month lives on the /calendar/events/ page. With nothing in
these two weeks the block stays hidden. Needs partials/events-home.hbs on the page.
*/
import afterLoad from './afterLoad';
import startRequest from './latestRequest';
import {
    dateKey,
    element,
    eventAnchor,
    fetchEvents,
    formatClock,
    groupByDay,
    hasExactDay,
    isLive,
    isRumored,
    isUnconfirmedPassed,
} from './eventsData';

const DAYS_PER_WEEK = 7;
const WEEKS = 2;
const CHIPS_PER_DAY = 2;

export default function eventsHome() {
    const root = document.querySelector('[data-events-home]');
    if (!root) {
        return;
    }
    afterLoad(() => showEvents(root));
}

async function showEvents(root) {
    const isLatest = startRequest(root);
    let events;
    try {
        events = await fetchEvents();
    } catch (error) {
        if (isLatest()) {
            // Also after a return from the back/forward cache: no stale "live" left on screen.
            console.warn('[events] calendar not loaded, home block hidden', error);
            root.hidden = true;
            root.querySelector('[data-events-weeks]').replaceChildren();
        }
        return;
    }
    if (!isLatest()) {
        return;
    }
    const now = new Date();
    const days = calendarDays(now);
    const byDay = groupByDay(events.filter((e) => hasExactDay(e) && !isUnconfirmedPassed(e)));
    if (!days.some((day) => byDay.has(dateKey(day)))) {
        root.hidden = true;
        return;
    }
    const pageUrl = root.querySelector('.th-home-section__all').href;
    const context = { byDay, now, todayKey: dateKey(now), pageUrl };
    const weeks = [];
    for (let week = 0; week < WEEKS; week += 1) {
        const start = week * DAYS_PER_WEEK;
        weeks.push(buildWeek(days.slice(start, start + DAYS_PER_WEEK), context));
    }
    root.querySelector('[data-events-weeks]').replaceChildren(...weeks);
    root.hidden = false;
}

/** Local midnights from Monday of this week, WEEKS weeks long. */
function calendarDays(now) {
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % DAYS_PER_WEEK));
    return Array.from({ length: WEEKS * DAYS_PER_WEEK }, (_, index) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + index);
        return day;
    });
}

function buildWeek(days, context) {
    const week = element('div', 'th-events-week');
    const range = element('p', 'th-events-week__range');
    range.append(
        element('span', '', shortDate(days[0])),
        element('span', '', `— ${shortDate(days[days.length - 1])}`),
    );
    const list = element('ol', 'th-events-week__days');
    days.forEach((day) => list.append(buildDay(day, context)));
    // A week with nothing from today on is dropped from the mobile agenda.
    week.dataset.ahead = String(days.some((day) => {
        const key = dateKey(day);
        return key >= context.todayKey && context.byDay.has(key);
    }));
    week.append(range, list);
    return week;
}

/** "Sep 28" — CSS sets the case. */
function shortDate(day) {
    return day.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
}

function buildDay(day, context) {
    const key = dateKey(day);
    const events = liveFirst(context.byDay.get(key) || [], context.now);
    const cell = element('li', 'th-events-day');
    cell.dataset.empty = String(events.length === 0);
    cell.dataset.today = String(key === context.todayKey);
    cell.dataset.past = String(key < context.todayKey);
    cell.dataset.live = String(events.some((event) => isLive(event, context.now)));
    cell.append(buildDayHead(day, key === context.todayKey));
    if (events.length === 0) {
        const none = element('span', 'th-events-day__none', '—');
        none.setAttribute('aria-hidden', 'true');
        cell.append(none);
    }
    events.slice(0, CHIPS_PER_DAY).forEach((event) => cell.append(buildChip(event, context)));
    if (events.length > CHIPS_PER_DAY) {
        const more = element('a', 'th-events-day__more', `+${events.length - CHIPS_PER_DAY} more`);
        // The first hidden event has an anchor on /calendar/events/ also when the day is past.
        more.href = `${context.pageUrl}#${eventAnchor(events[CHIPS_PER_DAY])}`;
        cell.append(more);
    }
    return cell;
}

/** An event on air now goes first, so it is never hidden behind "+N more". */
function liveFirst(events, now) {
    const onAir = (event) => Number(isLive(event, now));
    return [...events].sort((first, second) => onAir(second) - onAir(first));
}

function buildDayHead(day, isToday) {
    const head = element('p', 'th-events-day__head');
    head.append(
        element('span', 'th-events-day__weekday', day.toLocaleDateString('en-US', { weekday: 'short' })),
        element('span', 'th-events-day__month', day.toLocaleDateString('en-US', { month: 'short' })),
        element('span', 'th-events-day__number', String(day.getDate()).padStart(2, '0')),
    );
    if (isToday) {
        head.append(element('span', 'th-events-day__today', 'Today'));
    }
    return head;
}

function buildChip(event, context) {
    const chip = element('a', 'th-events-chip');
    chip.href = `${context.pageUrl}#${eventAnchor(event)}`;
    chip.append(
        element('span', 'th-events-chip__title', event.title),
        element('span', 'th-events-chip__meta', event.organizer || ''),
    );
    if (isLive(event, context.now)) {
        const live = element('span', 'th-events-chip__live');
        live.append(element('span', '', 'Live'), element('span', '', formatClock(event)));
        chip.append(live);
    } else if (isRumored(event)) {
        chip.append(element('span', 'th-events-chip__rumor', 'Rumored date'));
    }
    return chip;
}
